import React, { useState } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Download, Calendar, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Reports: React.FC = () => {
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');
  
  // Mock data for charts
  const revenueData = {
    labels: period === 'month' 
      ? ['1-7 Juin', '8-14 Juin', '15-21 Juin', '22-28 Juin']
      : period === 'quarter'
        ? ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin']
        : ['T1 2022', 'T2 2022', 'T3 2022', 'T4 2022', 'T1 2023', 'T2 2023'],
    datasets: [
      {
        label: 'Revenus',
        data: period === 'month'
          ? [5800, 7200, 6400, 8900]
          : period === 'quarter'
            ? [12500, 19200, 15700, 18900, 22400, 24100]
            : [45000, 52000, 48000, 61000, 68000, 72000],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
    ],
  };
  
  const ordersData = {
    labels: period === 'month' 
      ? ['1-7 Juin', '8-14 Juin', '15-21 Juin', '22-28 Juin']
      : period === 'quarter'
        ? ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin']
        : ['T1 2022', 'T2 2022', 'T3 2022', 'T4 2022', 'T1 2023', 'T2 2023'],
    datasets: [
      {
        label: 'Commandes',
        data: period === 'month'
          ? [12, 15, 13, 18]
          : period === 'quarter'
            ? [25, 39, 30, 41, 46, 52]
            : [85, 92, 88, 105, 115, 128],
        fill: false,
        borderColor: 'rgb(34, 197, 94)',
        tension: 0.1,
      },
    ],
  };
  
  const contractTypeData = {
    labels: ['Contrats Généraux', 'Contrats Commerciaux', 'Contrats de Travail', 'Propriété Intellectuelle', 'RGPD', 'Droit des Sociétés'],
    datasets: [
      {
        label: 'Répartition des ventes par type de contrat',
        data: [15, 32, 25, 10, 8, 10],
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };
  
  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPeriod(e.target.value as 'month' | 'quarter' | 'year');
  };
  
  const handleExportReport = (reportType: string) => {
    // In a real app, this would generate and download a report
    toast.success(`Rapport ${reportType} exporté avec succès`);
  };
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Rapports et statistiques</h1>
        
        <div className="flex items-center">
          <Calendar className="h-5 w-5 text-gray-400 mr-2" />
          <select
            className="block border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            value={period}
            onChange={handlePeriodChange}
          >
            <option value="month">Mois en cours</option>
            <option value="quarter">Semestre en cours</option>
            <option value="year">Année en cours</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Revenus</h2>
            <button
              onClick={() => handleExportReport('revenus')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Exporter
            </button>
          </div>
          <div className="h-80">
            <Bar 
              data={revenueData} 
              options={{ 
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: true,
                    text: 'Évolution des revenus',
                  },
                },
              }} 
            />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Commandes</h2>
            <button
              onClick={() => handleExportReport('commandes')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Exporter
            </button>
          </div>
          <div className="h-80">
            <Line 
              data={ordersData} 
              options={{ 
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: true,
                    text: 'Évolution des commandes',
                  },
                },
              }} 
            />
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Top des contrats vendus</h2>
            <button
              onClick={() => handleExportReport('top-contrats')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Exporter
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contrat
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ventes
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenus
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Freelance Overeenkomst
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Contrats de Travail
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    15
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    6 750 €
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Algemene Voorwaarden
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Contrats Commerciaux
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    12
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    8 100 €
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Geheimhoudingsovereenkomst (NDA)
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Contrats Commerciaux
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    10
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    3 750 €
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Arbeidsovereenkomst
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Contrats de Travail
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    8
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    4 400 €
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Verwerkersovereenkomst
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    RGPD & Protection des Données
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    7
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    3 850 €
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Répartition par catégorie</h2>
            <button
              onClick={() => handleExportReport('categories')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Exporter
            </button>
          </div>
          <div className="h-80 flex items-center justify-center">
            <Pie 
              data={contractTypeData} 
              options={{ 
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                  },
                },
              }} 
            />
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Rapports disponibles</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-2">Rapport de ventes mensuel</h3>
            <p className="text-sm text-gray-500 mb-4">
              Détail des ventes par jour, par contrat et par client pour le mois en cours.
            </p>
            <button
              onClick={() => handleExportReport('ventes-mensuel')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Télécharger (PDF)
            </button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-2">Rapport de performance</h3>
            <p className="text-sm text-gray-500 mb-4">
              Analyse des performances commerciales, taux de conversion et temps de traitement.
            </p>
            <button
              onClick={() => handleExportReport('performance')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Télécharger (Excel)
            </button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-2">Rapport clients</h3>
            <p className="text-sm text-gray-500 mb-4">
              Analyse de la clientèle, nouveaux clients, clients récurrents et valeur vie client.
            </p>
            <button
              onClick={() => handleExportReport('clients')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Télécharger (PDF)
            </button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-2">Rapport financier</h3>
            <p className="text-sm text-gray-500 mb-4">
              Revenus, marges, coûts et projections financières pour la période sélectionnée.
            </p>
            <button
              onClick={() => handleExportReport('financier')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Télécharger (Excel)
            </button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-2">Rapport de satisfaction</h3>
            <p className="text-sm text-gray-500 mb-4">
              Analyse des retours clients, évaluations et suggestions d'amélioration.
            </p>
            <button
              onClick={() => handleExportReport('satisfaction')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Télécharger (PDF)
            </button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-2">Rapport personnalisé</h3>
            <p className="text-sm text-gray-500 mb-4">
              Créez un rapport sur mesure en sélectionnant les métriques qui vous intéressent.
            </p>
            <button
              onClick={() => toast.success('Fonctionnalité de rapport personnalisé à implémenter')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Créer un rapport
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;